import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Phone, Search, Info, LogIn, LayoutDashboard } from 'lucide-react';
import { NavLink, Link } from 'react-router-dom';
import { useSimpleAuth } from '../../lib/authHelper';
import { NavItem } from './types';

const MotionNavLink = motion(NavLink);

interface MobileMenuProps {
  isMobileMenuOpen: boolean;
  navItems: NavItem[];
  currentPath: string;
  activeDropdown: string | null;
  setActiveDropdown: (name: string | null) => void;
  scrollToSection: (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, href: string) => void;
  WHATSAPP_NUMBER: string;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setIsMobileSearchFocused: (focused: boolean) => void;
  setIsMobileMenuOpen: (open: boolean) => void;
  mobileSearchRef: React.RefObject<HTMLDivElement>;
}

export default function MobileMenu({
  isMobileMenuOpen,
  navItems,
  currentPath,
  activeDropdown,
  setActiveDropdown,
  scrollToSection,
  WHATSAPP_NUMBER,
  searchQuery,
  setSearchQuery,
  setIsMobileSearchFocused,
  setIsMobileMenuOpen,
  mobileSearchRef
}: MobileMenuProps) {
  const user = useSimpleAuth();

  return (
    <AnimatePresence>
      {isMobileMenuOpen && (
        <motion.div
          id="mobile-menu"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden bg-white overflow-hidden border-t border-slate-200"
        >
          <motion.div 
            variants={{
              hidden: { opacity: 0 },
              show: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                  delayChildren: 0.2
                }
              }
            }}
            initial="hidden"
            animate="show"
            className="container mx-auto px-4 py-6 flex flex-col space-y-4"
            role="navigation"
            aria-label="قائمة التنقل للجوال"
          >
            {navItems.map((item) => (
              <motion.div 
                layout
                key={item.name}
                variants={{
                  hidden: { opacity: 0, x: 30 },
                  show: { opacity: 1, x: 0 }
                }}
                className="flex flex-col"
              >
                {item.hasDropdown ? (
                  <>
                    <button 
                      onClick={() => setActiveDropdown(activeDropdown === item.name ? null : item.name)}
                      aria-label={activeDropdown === item.name ? `إدراج قائمة ${item.name}` : `فتح قائمة ${item.name}`}
                      aria-expanded={activeDropdown === item.name}
                      aria-haspopup="menu"
                      className={`font-bold text-base p-3.5 rounded-xl flex items-center justify-between transition-all border ${
                        activeDropdown === item.name || (item.subItems?.some(s => s.href === currentPath))
                          ? 'text-brand-accent bg-brand-accent/5 border-brand-accent/20' 
                          : 'text-brand-dark bg-slate-50 border-slate-100'
                      }`}
                    >
                      {item.name}
                      <ChevronDown 
                        size={18} 
                        className={`transition-transform duration-300 ${activeDropdown === item.name || (item.subItems?.some(s => s.href === currentPath)) ? 'rotate-180 text-brand-accent' : 'text-brand-dark/40'}`}
                      />
                    </button>
                    <AnimatePresence>
                      {activeDropdown === item.name && (
                        <motion.div
                          layout
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                          role="menu"
                        >
                          <div className="flex flex-col py-2 px-1 gap-1.5 mt-1 border-r-2 border-slate-100 mr-2">
                            {item.subItems?.map((subItem, idx) => (
                              <MotionNavLink
                                layout
                                key={subItem.name}
                                to={subItem.href}
                                target={subItem.href.startsWith('http') ? '_blank' : '_self'}
                                rel={subItem.href.startsWith('http') ? 'noopener noreferrer' : ''}
                                onClick={(e) => scrollToSection(e, subItem.href)}
                                initial={{ opacity: 0, x: 15 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.04 }}
                                role="menuitem"
                                className={({ isActive }) => `px-4 py-3 rounded-xl text-right flex items-center gap-3.5 transition-all active:scale-[0.98] border border-transparent ${
                                  isActive 
                                    ? 'text-brand-accent bg-brand-accent/5 border-brand-accent/10 font-bold' 
                                    : 'text-slate-700 hover:text-brand-dark hover:bg-slate-50/80'
                                }`}
                              >
                                <span className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
                                  currentPath === subItem.href 
                                    ? 'bg-brand-accent text-white' 
                                    : 'bg-slate-100 text-brand-accent'
                                }`}>
                                  {subItem.icon ? <subItem.icon size={16} /> : <Info size={16} />}
                                </span>
                                <div className="flex flex-col text-right">
                                  <span className="text-sm font-bold leading-tight">{subItem.name}</span>
                                  {subItem.description && (
                                    <span className="text-[11px] text-slate-400 font-medium mt-0.5 leading-tight">
                                      {subItem.description}
                                    </span>
                                  )}
                                </div>
                              </MotionNavLink>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                ) : (
                  <NavLink 
                    to={item.href}
                    onClick={(e) => scrollToSection(e, item.href)}
                    className={({ isActive }) => `font-bold text-base p-3.5 rounded-xl transition-all block border ${
                      isActive 
                        ? 'text-brand-accent bg-brand-accent/5 border-brand-accent/20' 
                        : 'text-brand-dark bg-slate-50 border-slate-100 hover:bg-slate-100/50'
                    }`}
                  >
                    {item.name}
                  </NavLink>
                )}
              </motion.div>
            ))}
            
            <motion.a 
              href={`tel:${WHATSAPP_NUMBER}`}
              aria-label={`الاتصال الهاتفي بنا على الرقم ${WHATSAPP_NUMBER}`}
              variants={{
                hidden: { opacity: 0, x: 30 },
                show: { opacity: 1, x: 0 }
              }}
              className="flex items-center gap-2 text-brand-dark hover:text-brand-accent pt-4 transition-colors px-4"
            >
              <Phone size={18} />
              <span className="font-bold" dir="ltr">+{WHATSAPP_NUMBER}</span>
            </motion.a>

            <motion.div
              variants={{
                hidden: { opacity: 0, x: 30 },
                show: { opacity: 1, x: 0 }
              }}
              className="px-4 pt-2"
            >
              <Link 
                to={user ? (user.email === 'm.0543125382@gmail.com' ? '/admin/dashboard' : '/dashboard') : '/login'}
                onClick={() => setIsMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 w-full py-3 bg-[#1B4D3E] hover:bg-brand-accent text-white hover:text-brand-dark rounded-xl border border-brand-accent/20 font-bold text-sm transition-all duration-300"
              >
                {user ? <LayoutDashboard size={16} /> : <LogIn size={16} />}
                <span>{user ? 'لوحة التحكم' : 'تسجيل الدخول'}</span>
              </Link>
            </motion.div>

            <motion.div 
              variants={{
                hidden: { opacity: 0, x: 30 },
                show: { opacity: 1, x: 0 }
              }}
              className="relative pt-4 px-4"
              ref={mobileSearchRef}
              role="search"
            >
              <div className="relative">
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => {
                    setIsMobileSearchFocused(true);
                    setIsMobileMenuOpen(false);
                  }}
                  placeholder="بحث عن خدمة ..." 
                  aria-label="بحث عن خدمة"
                  className="w-full bg-slate-200 border border-white/20 text-brand-dark rounded-md py-3 px-4 pr-12 focus:outline-none focus:border-brand-accent transition-all"
                />
                <Search className="absolute right-4 bottom-3 text-slate-400" size={20} />
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
